import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { ConfigSourceInfo } from '../../services/deviceService';

interface ConfigSourcesPanelProps {
  effectiveConfig?: Record<string, unknown>;
  configSources?: Record<string, ConfigSourceInfo>;
  appliedTemplateIds?: number[];
  lastRequestAt?: string | null;
  lastRequestBy?: string | null;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') {
    return value.length > 60 ? value.slice(0, 57) + '…' : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 60 ? s.slice(0, 57) + '…' : s;
  } catch {
    return String(value);
  }
};

const maskIfSensitive = (key: string, value: unknown): string => {
  if (/pass|secret|token/i.test(key) && value !== null && value !== undefined && value !== '') {
    return '••••••';
  }
  return formatValue(value);
};

/**
 * Displays the per-field provenance of a device's effective configuration.
 *
 * Each row shows the field name, current value, the source layer, and —
 * when the source is a template — the template name and priority.
 *
 * Renders nothing when `configSources` is missing/empty AND no last-request
 * / applied-templates data is available, so it is safe to mount against
 * older backends.
 */
const ConfigSourcesPanel: React.FC<ConfigSourcesPanelProps> = ({
  effectiveConfig,
  configSources,
  appliedTemplateIds,
  lastRequestAt,
  lastRequestBy,
}) => {
  const { t, formatRelativeTime } = useI18n();

  // Hooks must be called unconditionally on every render (rules-of-hooks).
  // Early-exit rendering lives AFTER all hook calls below.

  // Deduplicate template list from configSources, preserving insertion order.
  const templateChips = React.useMemo(() => {
    if (!configSources) return [];
    const seen = new Set<number>();
    const out: Array<{ id: number; name: string; priority: number | null }> = [];
    for (const src of Object.values(configSources)) {
      if (src.layer === 'template' && src.template_id != null && !seen.has(src.template_id)) {
        seen.add(src.template_id);
        out.push({
          id: src.template_id,
          name: src.template_name || `#${src.template_id}`,
          priority: src.priority,
        });
      }
    }
    if (appliedTemplateIds) {
      for (const id of appliedTemplateIds) {
        if (!seen.has(id)) {
          seen.add(id);
          out.push({ id, name: `#${id}`, priority: null });
        }
      }
    }
    return out.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [configSources, appliedTemplateIds]);

  const sortedEntries = React.useMemo(() => {
    if (!configSources) return [];
    return Object.entries(configSources).sort(([a], [b]) => a.localeCompare(b));
  }, [configSources]);

  const hasSources =
    configSources && Object.keys(configSources).length > 0;

  if (!hasSources && !lastRequestAt && !appliedTemplateIds?.length) {
    return null;
  }

  const layerLabel = (layer: ConfigSourceInfo['layer']): string => {
    switch (layer) {
      case 'hardware_default': return t('configSources.layers.hardwareDefault');
      case 'device_column':    return t('configSources.layers.deviceColumn');
      case 'template':         return t('configSources.layers.template');
      case 'device_override':  return t('configSources.layers.deviceOverride');
      default:                 return layer;
    }
  };

  return (
    <div className="config-sources-panel">
      <h3>{t('configSources.title')}</h3>
      <p className="config-sources-description">{t('configSources.description')}</p>

      {(lastRequestAt || lastRequestBy) && (
        <div className="config-sources-lastseen">
          <div>
            <strong>{t('configSources.lastSeen')}:</strong>{' '}
            {lastRequestAt ? formatRelativeTime(lastRequestAt) : t('configSources.never')}
          </div>
          <div>
            <strong>{t('configSources.lastSeenBy')}:</strong>{' '}
            {lastRequestBy || '—'}
          </div>
        </div>
      )}

      {templateChips.length > 0 && (
        <>
          <div className="config-sources-applied-label">
            {t('configSources.appliedTemplates')}
          </div>
          <div className="config-sources-chips">
            {templateChips.map((tpl) => (
              <span
                key={tpl.id}
                className="config-sources-chip"
                title={tpl.priority != null ? t('configSources.priority', { value: tpl.priority }) : ''}
              >
                {tpl.name}
                {tpl.priority != null && (
                  <span className="config-sources-chip-priority">({tpl.priority})</span>
                )}
              </span>
            ))}
          </div>
        </>
      )}

      {hasSources ? (
        <div className="config-sources-table-wrapper">
          <table className="config-sources-table">
            <thead>
              <tr>
                <th>{t('configSources.field')}</th>
                <th>{t('configSources.value')}</th>
                <th>{t('configSources.layer')}</th>
                <th>{t('configSources.source')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(([fieldName, source]) => {
                const value = effectiveConfig?.[fieldName];
                return (
                  <tr key={fieldName}>
                    <td className="col-field">{fieldName}</td>
                    <td className="col-value">{maskIfSensitive(fieldName, value)}</td>
                    <td>
                      <span className={`config-sources-layer ${source.layer}`}>
                        {layerLabel(source.layer)}
                      </span>
                    </td>
                    <td className="col-source">
                      {source.layer === 'template' && source.template_name ? (
                        <>
                          {source.template_name}
                          {source.priority != null && (
                            <span style={{ marginLeft: 4, opacity: 0.8 }}>
                              ({t('configSources.priority', { value: source.priority })})
                            </span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="config-sources-empty">{t('configSources.noData')}</div>
      )}
    </div>
  );
};

export default ConfigSourcesPanel;
